// In production, we register a service worker to serve assets from local cache.

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on the "N+1" visit to a page, since previously
// cached resources are updated in the background.

// To learn more about the benefits of this model, read https://goo.gl/KwvDNy.
// This link also includes instructions on opting out of this behavior.

// eslint-disable-next-line no-console

const logger =
  (method) =>
  (...args) =>
    void console[method]('[', 'registerServiceWorker', ']', '🕸️👷', ...args);

const log = logger('log');
const warn = logger('warn');
const error = logger('error');

const { serviceWorker } = navigator || {};

const onNewServiceWorker = (registration, callback) => {
  if (registration.waiting) {
    // Worker is waiting to activate. Can occur if multiple clients open and one of them is refreshed
    return callback();
  }

  const listen = () => {
    registration.installing.addEventListener('statechange', (event) => {
      log('onNewServiceWorker()', 'listen()', 'event.state:', event.state);
      if (event.target.state === 'installed') {
        // New service worker is available
        callback();
      }
    });
  };

  if (registration.installing) {
    return listen();
  }

  // We are currently controlled so a new worker may be found...
  // Add a listener in case a new worker is found,
  registration.addEventListener('updatefound', listen);
  return () => void 1;
};

const registerValidSW = (swUrl) => {
  const TAG = 'registerValidSW()';

  if (!serviceWorker) {
    log(TAG, 'No navigator.serviceWorker:', serviceWorker);
    return;
  }

  // let preventDevToolsReloadLoop = false;
  serviceWorker.ready.then((registration) => {
    log(TAG, 'before delete registration', registration);
    registration.unregister();
    caches.keys().then((keys) => {
      keys.forEach((key) => {
        log(TAG, 'delete cache key:', key, 'registration:', registration);
        caches.delete(key);
      });
      log(TAG, 'after delete registration', registration);
    });
  });

  serviceWorker
    .register(swUrl)
    .then((registration) => {
      log(TAG, 'before update registration', registration);

      // eslint-disable-next-line promise/no-nesting
      registration.update().catch((e) => warn(TAG, e));

      // eslint-disable-next-line no-param-reassign
      registration.onupdatefound = () => {
        log(TAG, 'update found');

        const installingWorker = registration.installing;
        installingWorker.onstatechange = () => {
          const { state } = installingWorker;

          log(TAG, '🧑 installingWorker.state:', state);

          if (state === 'installed' || state === 'activate') {
            if (serviceWorker.controller) {
              // The old content will is purged and fresh content will addded to the cache
              onNewServiceWorker(registration, () => {
                log(TAG, 'skipWaiting');
                registration.waiting.postMessage('skipWaiting');
              });
            } else {
              // Everything has been precached
              log(TAG, 'Content is cached for offline use');
            }
          }
        };
      };
    })
    .catch((e) => void error(TAG, 'Error during service worker registration:', e));
};

const checkValidServiceWorker = (
  swUrl, // Check if the service worker can be found. If it can't reload the page.
) =>
  void fetch(swUrl)
    .then((response) => {
      // Ensure service worker exists, and that we really are getting a JS file.
      if (response.status === 404 || response.headers.get('content-type').indexOf('javascript') === -1) {
        log('checkValidServiceWorker()', 'No service worker found');
        // eslint-disable-next-line promise/no-nesting
        serviceWorker.ready.then(
          // eslint-disable-next-line promise/no-nesting
          (registration) => void registration.unregister().then(() => log('checkValidServiceWorker()', 'new release')),
        );
      } else {
        log('checkValidServiceWorker()', 'Found service worker');
        registerValidSW(swUrl);
      }
    })
    .catch(() => log('checkValidServiceWorker()', 'No internet connection found. App is running in offline mode.'));

const registerServiceWorker = (conf) => {
  if (!conf.registerServiceWorker) return;

  if (!serviceWorker) {
    return;
  }

  const { location } = window;
  const publicUrl = process.env.PUBLIC_URL;

  if (new URL(publicUrl, location).origin !== location.origin) {
    // Worker won't work if PUBLIC_URL is on a different origin from what the page is served on.
    // This might happen if a CDN is used to serve assets;
    // @SEE: // https://github.com/facebookincubator/create-react-app/issues/2374
    return;
  }

  window.addEventListener('load', () => {
    const workerUrl = `${publicUrl}/service-worker.js`;
    const { hostname } = location;

    const isLocalhost = !!(
      hostname === 'localhost' || // [::1] is the IPv6 localhost address.
      hostname === '[::1]' || // 127.0.0.1/8 is considered localhost for IPv4.
      hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
    );

    if (isLocalhost) {
      log('register()', 'load()', 'Running on localhost');
      checkValidServiceWorker(workerUrl);
    } else {
      log('register()', 'load()', 'Not running on localhost');
      registerValidSW(workerUrl);
    }
  });
};

export const unregister = () => void (serviceWorker && serviceWorker.ready.then((registration) => void registration.unregister()));

export default registerServiceWorker;
